import { Component, effect, OnInit, signal, ViewChild, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
    ClauseLibraryService,
    IClause,
    IClauseBundle,
    CLAUSE_SOURCE,
    IDuplicateClausesCluster,
} from '../../../../../../services/clause-library.service';
import { ToastService } from '../../../../../../../@core/services/toast.service';
import { ButtonComponent } from '../../../../../../../@core/components/ui/button/button.component';
import { CheckboxComponent } from '../../../../../../../@core/components/ui/checkbox/checkbox.component';
import { SelectComponent, SelectOption, SelectConfig } from '../../../../../../../@core/components/ui/select';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogComponent } from 'src/app/@core/components/ui/dialog/dialog.component';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ClauseBundleService } from 'src/app/contract-management/services/clause-bundle.service';
import { TooltipModule } from 'primeng/tooltip';
import { FormatTextPipe } from '../../../../../../../@shared/pipes/formated-text.pipe';
import { TextCasePipe } from '../../../../../../../@shared/pipes/textCase.pipe';
import { DynamicComponentRenderer } from '../../../../../../../@shared/directives/dynamic-component-renderer.directive';
import { Merge } from 'lucide-angular';
import { IconComponent, IconSourceEnum } from 'src/app/@core/components/ui/icon/icon.component';
import { DataTableComponent } from '../../../../../../../@shared/components/data-table/data-table.component';
import { TableColumn, TableConfig } from '../../../../../../../@shared/models/table.models';
import { DeleteConfirmationComponent } from '../../../../../../../@core/components/ui/delete-confirmation/delete-confirmation.component';
import { UserNameDisplayComponent } from "src/app/@shared/components/user-name-display";

const UNBUNDLED_VALUE = '';

@Component({
    selector: 'app-duplicate-clauses',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ButtonComponent,
        CheckboxComponent,
        SelectComponent,
        ConfirmDialogModule,
        DialogComponent,
        ProgressSpinnerModule,
        TooltipModule,
        FormatTextPipe,
        TextCasePipe,
        DynamicComponentRenderer,
        IconComponent,
        DataTableComponent,
        DeleteConfirmationComponent,
        UserNameDisplayComponent
    ],
    providers: [ConfirmationService],
    templateUrl: './duplicate-clauses.component.html',
    styleUrl: './duplicate-clauses.component.scss',
})
export class DuplicateClausesComponent implements OnInit {
    @ViewChild('dataTable') dataTableRef!: DataTableComponent;
    @ViewChild('clauseNameTemplateRef') clauseNameTemplateRef!: TemplateRef<any>;
    @ViewChild('clauseTextTemplateRef') clauseTextTemplateRef!: TemplateRef<any>;
    @ViewChild('sourceTemplateRef') sourceTemplateRef!: TemplateRef<any>;
    @ViewChild('createdByTemplateRef') createdByTemplateRef!: TemplateRef<any>;
    @ViewChild('createdAtTemplateRef') createdAtTemplateRef!: TemplateRef<any>;

    selectedClauseIds: string[] = [];
    isSelectAll = false;

    showMergeDialog = false;
    showDeleteConfirmDialog = false;
    mergeClauses: IClause[] = [];
    mergeClausesSelected = new Set<string>();
    mergeSuggestionLoading = false;
    mergeSuggestion: { name: string; text: string } = { name: '', text: '' };
    selectedMergeClausesChanged = false;
    savingMergeClause = false;

    expandClauseTextCell = false;
    expandedClauseTextContent = '';
    expandedClauseTextTitle = '';

    selectedBundleId = '';
    clauseBundleOptions: SelectOption[] = [];
    bundleSelectConfig: SelectConfig = {
        placeholder: 'Select Clause Bundle',
        searchable: true,
        clearable: false,
        styleClass: 'font-semibold rounded-xl text-black border border-gray-200',
        appendTo: 'body',
    };

    duplicateClusterLoading = false;
    duplicateClausesData: IClause[] = [];
    duplicateClusters: IDuplicateClausesCluster[] = [];
    duplicateClusterOptions: { clusterId: string; title: string; count: number }[] = [];
    selectedDuplicateCluster = signal<{ clusterId: string; title: string } | undefined>(undefined);

    // icons
    IconSource = IconSourceEnum;

    duplicateClausesTableColumns: TableColumn[] = [
        { field: 'name', header: 'Clause name', type: 'custom', template: 'clauseNameTemplate' },
        { field: 'text', header: 'Clause text', type: 'custom', template: 'clauseTextTemplate' },
        { field: 'source', header: 'Source', type: 'custom', template: 'sourceTemplate' },
        { field: 'createdBy', header: 'Created by', type: 'custom', template: 'createdByTemplate' },
        { field: 'createdAt', header: 'Created date', type: 'custom', template: 'createdAtTemplate' },
    ];

    get duplicateClausesTableConfig(): TableConfig {
        return {
            selectable: true,
            paginator: false,
            rows: 10,
            loading: this.duplicateClusterLoading,
            emptyMessage: 'No clauses in this cluster',
            globalFilter: false,
            selectionOnRowClick: false,
        };
    }

    get templatesMap(): { [key: string]: TemplateRef<any> } {
        return {
            clauseNameTemplate: this.clauseNameTemplateRef,
            clauseTextTemplate: this.clauseTextTemplateRef,
            sourceTemplate: this.sourceTemplateRef,
            createdByTemplate: this.createdByTemplateRef,
            createdAtTemplate: this.createdAtTemplateRef,
        };
    }

    constructor(
        private clauseLibraryService: ClauseLibraryService,
        private clauseBundleService: ClauseBundleService,
        private toast: ToastService,
        private confirmationService: ConfirmationService
    ) {
        effect(() => {
            const selected = this.selectedDuplicateCluster();
            const cluster = this.duplicateClusters.find((c) => c._id === selected?.clusterId);
            const clauses = cluster?.clauses ?? [];
            this.duplicateClausesData = clauses.map((c) =>
                typeof c === 'string' ? ({ _id: c, name: '' } as IClause) : c
            );
            this.selectedClauseIds = [];
            this.isSelectAll = false;
            setTimeout(() => this.dataTableRef?.clearSelection(), 0);
        });
    }

    ngOnInit() {
        this.getClauseBundles();
    }

    getClauseBundles() {
        this.clauseBundleService.getBundles({}, false).subscribe((res: any) => {
            if (res && res.success) {
                const bundleOptions: SelectOption[] = (res.data.bundles || []).map((bundle: IClauseBundle) => ({
                    label: bundle.name,
                    value: bundle._id
                }));
                this.clauseBundleOptions = [
                    ...bundleOptions,
                    { label: 'Unbundled', value: UNBUNDLED_VALUE }
                ];
                this.selectedBundleId = this.clauseBundleOptions[0]?.value ?? '';
                this.getDuplicateClusters();
            }
        });
    }

    getDuplicateClusters() {
        this.duplicateClusterLoading = true;
        const bundleId = this.selectedBundleId === UNBUNDLED_VALUE ? undefined : this.selectedBundleId;
        this.clauseLibraryService.getDuplicateClauses(bundleId).subscribe({
            next: (res) => {
                this.duplicateClusterLoading = false;
                if (res?.success && Array.isArray(res.data)) {
                    this.duplicateClusters = res.data;
                    this.duplicateClusterOptions = this.duplicateClusters.map((cluster) => {
                        const first = cluster.clauses?.[0];
                        const name = typeof first === 'object' && first?.name ? first.name : 'Clause';
                        return {
                            clusterId: cluster._id,
                            title: name,
                            count: cluster.clauses?.length ?? 0,
                        };
                    });
                    this.selectedDuplicateCluster.set(this.duplicateClusterOptions[0]);
                } else {
                    this.duplicateClusters = [];
                    this.duplicateClusterOptions = [];
                    this.selectedDuplicateCluster.set(undefined);
                }
            },
            error: () => {
                this.duplicateClusterLoading = false;
                this.duplicateClusters = [];
                this.duplicateClusterOptions = [];
                this.selectedDuplicateCluster.set(undefined);
            },
        });
    }

    selectBundle(bundleId: string) {
        this.selectedBundleId = bundleId;
        this.selectedClauseIds = [];
        this.isSelectAll = false;
        this.getDuplicateClusters();
    }

    selectDuplicateCluster(cluster: { clusterId: string; title: string }) {
        this.selectedDuplicateCluster.set(cluster);
    }

    onDuplicateClausesSelectionChange(rows: IClause[]) {
        this.selectedClauseIds = rows.map((c) => c._id);
        this.isSelectAll =
            this.duplicateClausesData.length > 0 && rows.length === this.duplicateClausesData.length;
    }

    getDuplicateClausesRowClass(rowData: IClause): string {
        return this.selectedClauseIds.includes(rowData._id) ? 'bg-arena-orange-100 hover:bg-arena-orange-100' : '';
    }

    getClauseName(clause: IClause): string {
        return clause.name || '-';
    }

    getClauseText(clause: IClause): string {
        return clause.text ?? '';
    }

    openClauseTextModal(content: string, title: string) {
        this.expandClauseTextCell = true;
        this.expandedClauseTextContent = content;
        this.expandedClauseTextTitle = title;
    }

    getClauseBundleName(clause: IClause): string {
        if (!clause.clauseBundles || clause.clauseBundles.length === 0) return '-';
        const first = clause.clauseBundles[0];
        return typeof first === 'string' ? first : first?.name || '-';
    }

    getSourceLabel(source: CLAUSE_SOURCE): string {
        switch (source) {
            case CLAUSE_SOURCE.MANUAL:
                return 'Manual entry';
            case CLAUSE_SOURCE.EXCEL:
                return 'Excel';
            case CLAUSE_SOURCE.EXTRACTED:
                return 'Extracted';
            default:
                return source || '-';
        }
    }

    getCreatedByName(clause: IClause): string {
        if (typeof clause.createdBy === 'string') return clause.createdBy;
        return clause.createdBy?.fullName || clause.createdBy?.email || 'Unknown';
    }

    markNotDuplicateClause(event: Event) {
        const isPlural = this.selectedClauseIds.length > 1;
        this.confirmationService.confirm({
            target: event.target as EventTarget,
            header: isPlural ? 'Mark as Not Duplicates?' : 'Mark as Not Duplicate?',
            message: isPlural
                ? 'Are you sure these clauses are not duplicates? They will be retained as separate clauses.'
                : 'Are you sure this clause is not a duplicate? It will be retained as a separate clause.',
            acceptLabel: 'Confirm',
            rejectLabel: 'Cancel',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.clauseLibraryService.updateClausesAsNotDuplicate(this.selectedDuplicateCluster()?.clusterId ?? '', this.selectedClauseIds).subscribe((res) => {
                    if (res?.success) {
                        this.getDuplicateClusters();
                        this.toast.success('Clauses marked as not duplicate successfully.');
                    } else {
                        this.toast.error('Failed to mark clauses as not duplicate.');
                    }
                });
            },
        });
    }

    acceptClause(event: Event) {
        this.confirmationService.confirm({
            target: event.target as EventTarget,
            header: 'Accept as Final Clause?',
            message: 'Are you sure you want to keep this clause and discard the others in the cluster?',
            acceptLabel: 'Confirm',
            rejectLabel: 'Cancel',
            icon: 'pi pi-exclamation-triangle',
            acceptButtonProps: {
                variant: 'filled',
                severity: 'primary',
            },
            accept: () => {
                const clauseId = this.selectedClauseIds[0];
                this.clauseLibraryService.acceptClauseInCluster(this.selectedDuplicateCluster()?.clusterId ?? '', clauseId).subscribe((res) => {
                    if (res?.success) {
                        this.getDuplicateClusters();
                        this.toast.success('Clause accepted for duplicates successfully.');
                    } else {
                        this.toast.error('Failed to accept clause.');
                    }
                });
            },
        });
    }

    deleteClauses() {
        if (this.selectedClauseIds.length === 0) return;
        this.showDeleteConfirmDialog = true;
    }

    confirmDeleteClauses() {
        const count = this.selectedClauseIds.length;
        const isPlural = count > 1;
        this.showDeleteConfirmDialog = false;
        this.clauseLibraryService.deleteClausesFromCluster(this.selectedDuplicateCluster()?.clusterId ?? '', this.selectedClauseIds).subscribe((res) => {
            if (res?.success) {
                this.toast.success(`${count} clause${isPlural ? 's' : ''} deleted for duplicates successfully.`);
                this.selectedClauseIds = [];
                this.isSelectAll = false;
                this.getDuplicateClusters();
            } else {
                this.toast.error('Failed to delete clauses for duplicates.');
            }
        });
    }

    openMergeDialog() {
        const selectedSet = new Set(this.selectedClauseIds);
        this.mergeClauses = this.duplicateClausesData.filter((c) => selectedSet.has(c._id));
        if (this.mergeClauses.length < 2) {
            this.toast.error('Please select at least two clauses to merge.');
            return;
        }
        this.mergeClausesSelected.clear();
        this.mergeClauses.forEach((c) => this.mergeClausesSelected.add(c._id));
        this.showMergeDialog = true;
        this.getMergeSmartSuggestion();
    }

    getMergeSmartSuggestion() {
        this.mergeSuggestionLoading = true;
        const selectedIds = this.mergeClauses.filter((c) => this.mergeClausesSelected.has(c._id)).map((c) => c._id);
        this.clauseLibraryService.getMergeClauseSuggestion(selectedIds).subscribe({
            next: (res) => {
                if (res?.success && res?.data) {
                    this.mergeSuggestion = {
                        name: res.data.clauseName ?? '',
                        text: res.data.clauseText ?? '',
                    };
                } else {
                    this.mergeSuggestion = { name: '', text: '' };
                }
                this.selectedMergeClausesChanged = false;
                this.mergeSuggestionLoading = false;
            },
            error: () => {
                this.mergeSuggestion = { name: '', text: '' };
                this.mergeSuggestionLoading = false;
            },
        });
    }

    selectMergeClause(clauseId: string) {
        this.selectedMergeClausesChanged = true;
        if (this.mergeClausesSelected.has(clauseId)) {
            this.mergeClausesSelected.delete(clauseId);
        } else {
            this.mergeClausesSelected.add(clauseId);
        }
    }

    closeMergeDialog() {
        this.showMergeDialog = false;
        this.mergeClauses = [];
        this.mergeClausesSelected.clear();
        this.mergeSuggestionLoading = false;
        this.mergeSuggestion = { name: '', text: '' };
        this.selectedClauseIds = [];
        this.isSelectAll = false;
    }

    saveMergeClausePrompt() {
        this.confirmationService.confirm({
            header: 'Approve & Save?',
            message:
                'This action will discard all other clauses in the cluster and replace them with the merged clause. Do you wish to continue?',
            acceptLabel: 'Confirm',
            rejectLabel: 'Cancel',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.saveMergeClause(true);
            },
            reject: () => { },
        });
    }

    saveMergeClause(accept = false) {
        this.savingMergeClause = true;
        let clauseIds: string[];
        if (accept) {
            clauseIds = this.mergeClauses.map((c) => c._id);
        } else {
            clauseIds = this.mergeClauses.filter((c) => this.mergeClausesSelected.has(c._id)).map((c) => c._id);
        }
        this.clauseLibraryService
            .createMergedClause({
                clusterId: this.selectedDuplicateCluster()?.clusterId ?? '',
                name: this.mergeSuggestion.name,
                text: this.mergeSuggestion.text,
                clauseIds,
                accept,
            })
            .subscribe({
                next: (res) => {
                    this.savingMergeClause = false;
                    if (res?.success) {
                        this.closeMergeDialog();
                        this.toast.success('Clauses merged successfully.');
                        this.getDuplicateClusters();
                    } else {
                        this.toast.error(res?.message ?? 'Failed to merge clauses.');
                    }
                },
                error: () => {
                    this.savingMergeClause = false;
                    this.toast.error('Failed to merge clauses.');
                },
            });
    }

    handleConfirmAccept(message: { accept?: () => void }) {
        if (message.accept) message.accept();
        this.confirmationService.close();
    }

    handleConfirmReject(message: { reject?: () => void }) {
        if (message.reject) message.reject();
        this.confirmationService.close();
    }

    formatDate(date: string | Date | undefined): string {
        if (!date) return '-';
        const d = new Date(date);
        const day = d.getDate();
        const month = d.toLocaleString('en-US', { month: 'long' });
        const year = d.getFullYear();

        const suffix = (day: number) => {
            if (day > 3 && day < 21) return 'th';
            switch (day % 10) {
                case 1: return 'st';
                case 2: return 'nd';
                case 3: return 'rd';
                default: return 'th';
            }
        };

        return `${day}${suffix(day)} ${month} ${year}`;
    }
}
